import { Link } from '@tanstack/react-router'
import type { LockedMatchSummary } from '@workspace/api-backend'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { Briefcase, Building2, Landmark, ScanEye, Sparkles } from 'lucide-react'

interface LockedMatchesFooterProps {
  summary: LockedMatchSummary
  startupId: string
}

// Blurred placeholder rows so the founder sees the rough shape of "more
// matches exist" without leaking real investor data. Generic Lucide icons.
const MOCK_ROWS: {
  name: string
  pills: string[]
  Icon: typeof Building2
  iconClass: string
}[] = [
  {
    name: 'Investor A',
    pills: ['Seed', 'Fintech', 'NYC', '$500k–$2M'],
    Icon: Building2,
    iconClass: 'bg-primary/10 text-primary border-primary/20',
  },
  {
    name: 'Investor B',
    pills: ['Pre-seed', 'B2B SaaS', 'SF', '$250k–$1M'],
    Icon: Briefcase,
    iconClass: 'bg-secondary/40 text-secondary-foreground border-border',
  },
  {
    name: 'Investor C',
    pills: ['Seed', 'Climate', 'EU', '$1M–$3M'],
    Icon: Landmark,
    iconClass: 'bg-muted text-muted-foreground border-border',
  },
]

export function LockedMatchesFooter({ summary, startupId }: LockedMatchesFooterProps) {
  // Lead with the total; surface fit-quality anchors as secondary lines.
  // Strong fit gets its own line when present; medium fit gets a line when
  // present so weaker matcher outputs still get a quality anchor instead
  // of just a raw total.
  const headlineLead = `${summary.totalRemaining} more matches`
  const headlineSubs = [
    summary.strongFitRemaining > 0 ? `${summary.strongFitRemaining} strong fit` : null,
    summary.mediumRemaining > 0 ? `${summary.mediumRemaining} medium fit` : null,
  ].filter((x): x is string => x !== null)
  const otherCount = summary.totalRemaining - 1

  return (
    <section className="mt-10" aria-labelledby="locked-matches-heading">
      <div className="rounded-xl border border-primary/30 bg-primary/5 px-5 py-5">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="flex-1">
            <h2 id="locked-matches-heading" className="text-sm font-semibold text-foreground">
              {headlineLead}
            </h2>
            {headlineSubs.length > 0 && (
              <p className="mt-0.5 text-xs text-muted-foreground">({headlineSubs.join(', ')})</p>
            )}
            <p className="mt-2 text-xs leading-relaxed text-foreground/80">
              {summary.totalRemaining * 3}+ partner contacts, each with a ready-to-send outreach
              message personalized to that partner.
            </p>
            {summary.teaserInvestor && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-background/40 px-2.5 py-1.5">
                {summary.teaserInvestor.logoUrl ? (
                  <img
                    src={summary.teaserInvestor.logoUrl}
                    alt={`${summary.teaserInvestor.name} logo`}
                    className="size-5 rounded-sm border border-border/60 bg-card object-cover"
                  />
                ) : (
                  <div className="flex size-5 items-center justify-center rounded-sm border border-border/60 bg-card text-[9px] font-semibold text-muted-foreground">
                    {summary.teaserInvestor.name
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((w) => w[0]?.toUpperCase() ?? '')
                      .join('')}
                  </div>
                )}
                <span className="text-xs text-foreground/80">
                  Top hidden match:{' '}
                  <span className="font-semibold">{summary.teaserInvestor.name}</span>
                  {otherCount > 0 ? ` (+ ${otherCount} others)` : ''}
                </span>
              </div>
            )}
            <div className="mt-4">
              <Link
                to="/app/startup/$id/outreach"
                params={{ id: startupId }}
                className={ctaLinkClasses({ size: 'lg' })}
              >
                <span>Set up outreach to unlock</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="pointer-events-none select-none space-y-3 blur-sm" aria-hidden="true">
          {MOCK_ROWS.map(({ name, pills, Icon, iconClass }) => (
            <div
              key={name}
              className="space-y-3 rounded-xl border border-border/60 bg-card/30 px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex size-10 shrink-0 items-center justify-center rounded-md border ${iconClass}`}
                >
                  <Icon className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground/80">{name}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {pills.map((p) => (
                      <span
                        key={p}
                        className="rounded border border-border/50 bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Not a button — the teaser is locked. It still borrows the CTA
                    classes so the preview looks like the real thing. */}
                <span className={ctaLinkClasses({ size: 'sm' })}>
                  <ScanEye className="size-3.5" />
                  See their take
                </span>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Thesis aligns with your stage and sector. Recent portfolio overlap suggests strong
                conviction in adjacent execution patterns and a credible path to a warm intro.
              </p>
              <div className="flex flex-wrap gap-1">
                {['Acme', 'Northwind', 'Globex', 'Initech', 'Soylent', 'Umbrella'].map((p) => (
                  <span
                    key={p}
                    className="rounded border border-border/50 bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
